import React, { useEffect, useState } from 'react';
import { Image as ImageIcon } from 'lucide-react';

// Module-level cache keyed by `src` so multiple AuthImage instances pointing
// at the same URL only fetch once per session. Cleared on full reload.
const _blobCache = new Map();

function backendBase() {
  return (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
}

function authHeader() {
  const tok = (typeof window !== 'undefined' && localStorage.getItem('token')) || '';
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

// Resolve a media URL into a fully-qualified URL we can fetch with the token.
function resolveUrl(src) {
  if (!src) return '';
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:') || src.startsWith('blob:')) {
    return src;
  }
  const base = backendBase();
  if (src.startsWith('/api/')) return `${base}${src}`;
  if (src.startsWith('/')) return `${base}${src}`;
  return src;
}

// AuthImage — renders a thumbnail that requires the JWT auth header to load.
// Falls back to an icon placeholder while loading and on error.
//
// Props:
//   src        — image URL (relative /api/... or absolute)
//   alt        — alt text
//   className  — applied to the resolved <img>
//   placeholder — optional element to render while loading / on failure
export default function AuthImage({ src, alt = '', className = '', placeholder = null }) {
  const [resolved, setResolved] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let aborted = false;
    setFailed(false);
    if (!src) {
      setResolved('');
      return () => { aborted = true; };
    }
    const url = resolveUrl(src);
    // Public/data/blob URLs — use directly, no fetch.
    if (url.startsWith('data:') || url.startsWith('blob:') || url.includes('/cloudfront.net') || /\.(jpe?g|png|webp|gif|svg)(\?|$)/i.test(url)) {
      // Heuristic: if it's a direct public file URL, just point at it.
      // Server-proxied paths still need bearer-fetch, handled below.
      if (url.startsWith('data:') || url.startsWith('blob:')) {
        setResolved(url);
        return () => { aborted = true; };
      }
    }
    // Cache hit
    if (_blobCache.has(url)) {
      setResolved(_blobCache.get(url));
      return () => { aborted = true; };
    }
    // Bearer-fetch the bytes and create a blob URL so the <img> tag can render.
    (async () => {
      try {
        const res = await fetch(url, { headers: authHeader() });
        if (!res.ok) {
          if (!aborted) setFailed(true);
          return;
        }
        const blob = await res.blob();
        const obj = URL.createObjectURL(blob);
        _blobCache.set(url, obj);
        if (!aborted) setResolved(obj);
      } catch (e) {
        if (!aborted) setFailed(true);
      }
    })();
    return () => { aborted = true; };
  }, [src]);

  if (!resolved || failed) {
    return (
      placeholder !== null ? placeholder : (
        <div className={`flex items-center justify-center bg-zinc-900 ${className}`} data-testid="auth-image-placeholder">
          <ImageIcon className="w-4 h-4 text-zinc-700" />
        </div>
      )
    );
  }
  return (
    <img
      src={resolved}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
      data-testid="auth-image"
    />
  );
}
